import React from "react";
import {connect} from "react-redux";
import _ from "lodash";
import clsx from "clsx";
import moment from "moment";

import withStyles from "@material-ui/core/styles/withStyles";
import Grid from "@material-ui/core/Grid";

import styles from "../styles";
import PrimaryButton from "../../../../../../../components/Buttons/PrimaryButton";
import StepFooter from "../../StepFooter/StepFooter";
import {
  INSTRUMENT_SOURCE_TYPE, INSTRUMENT_SOURCE_TYPE_LABEL,
  SAVE_AND_CONTINUE_BTN_LABEL
} from "../../../../../constants";
import {
  calculateInstrumentsAverageSRRI,
  EinzeltitelTradingInstrumentsHandler,
  getRiskProfilingAnswers,
  getRiskScoreConfirmation, instrumentNeedsRiskReasoning, instrumentsSourceSrriValidationRequired,
  InvestmentStrategyHandler,
  ModelPortfolioHandler, splitEinzeltitelTradingInstruments
} from "../../../../../utils";
import {
  displayErrorSnackBar, displayWarningSnackBar,
} from "../../../../../../../components/SnackbarProvider/actions";
import {useConfirmationModalContext} from "../../../../../../../components/ConfirmationModalContextProvider";
import {CircularProgress} from "@material-ui/core";
import { getErrorMessage } from '../../../../../../../utils/utils';
import {validateEinzeltitelLimit} from "../../../../../../Trades/constants";
import {instrCustodianData} from "../../../../../../Trades/utils";
import {DATE_FORMAT} from "../../../../../../../utils/constants";
import {setProductSelectionInvestmentRecomendation} from "../../../../../actions";
import AssetsSelection from "./components/AssetsSelection";
import RiskProfileCardForProductSelection from "./components/RiskProfileCardForProductSelection";
import InfoCard from "../DynamicStep/components/InfoCard";

const checkNewDesignPlans = (plans, dataService, question) => {
  if (!_.isEmpty(plans)) {
    const bankConfig = dataService.getBankConfig();
    const spErrors = new Set();
    const fromDateOptions = (bankConfig.savings_plan_execution_dates || []).map(option => option.value);
    plans.forEach(plan => {
      const planData = plan.savingPlanData;
      // clean error
      planData.from_date_error = planData.rotation_error = planData.till_date_error = undefined;

      // validate plan
      if (!bankConfig.onboarding_savings_plan_periodic_plans.includes(planData.rotation.value)) {
        spErrors.add('Bitte wählen Sie einen anderen Turnus.');
        planData.rotation_error = true;
      }
      if (!fromDateOptions.includes(moment(planData.from_date, DATE_FORMAT).date())) {
        spErrors.add('Bitte wählen Sie ein anderes Startdatum.');
        planData.from_date_error = true;
      }
      if (planData.till_date && !fromDateOptions.includes(moment(planData.till_date, DATE_FORMAT).date())) {
        spErrors.add('Bitte wählen Sie ein anderes Datum für die letzte Ausführung.');
        planData.till_date_error = true;
      }
    });

    if (spErrors.size) {
      question.funds_error = [...spErrors].join('\n');
    }
  }
};

const ProductSelectionStepV2 = props => {
  const {
    classes,
    custom_classes,
    uid,
    name,
    questions: [
      instrumentsQuestion,
      sourceQuestion,
      portfolioBuilderQuestion,
    ],
    help_text,
    info_text,
    onNextClick,
    onAnswerChange,
    dataService,
    extraRightButtons,
  } = props;

  const [selectedDepot, setDepot] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const modalContext = useConfirmationModalContext();
  const [instrumentsHandler, setInstrumentsHandler] = React.useState(null);
  const [stepsData, setStepsData] = React.useState(null);

  const summaryStep = React.useMemo(() => {
    return dataService.getStep("product-selection-summary")
  }, []);

  const displayInstrumentsErrors = React.useCallback((errors) => {
    if(!_.isEmpty(errors)){
      props.dispatch(displayErrorSnackBar(errors.join("\n")));
    }
  }, []);

  React.useEffect(() => {

    const investmentsData = dataService.getInvestedAmountData();
    const savingsPlan = investmentsData.savings_plan;
    const singleBuyAmount = investmentsData.single_investment;
    const vlPlanEnabled = investmentsData.vl_plan_enabled;
    const isOpenOnly = dataService.isOpenOnly;
    const showFunds = dataService.showFunds();
    const depot = dataService.getDepot(true);
    if(!depot) return;

    (async () => {
      setLoading(true);
      await dataService.syncDepotTypeInfo();
      setDepot(depot);

      const handler = new EinzeltitelTradingInstrumentsHandler(
        depot.bankCompanyId,
        dataService._srri,
        dataService._target_market,
        !investmentsData.einzeltitel_enabled,
        vlPlanEnabled,
        dataService.bank,
        dataService.user && dataService.user.bca_level_id,
        dataService.serviceConcept,
        getRiskProfilingAnswers(dataService, dataService.bank && dataService.bank.available_asset_classes || {}),
        !showFunds);
      setInstrumentsHandler(handler);
      const isPIProcess = dataService.isPIProcess();
      const isMPProcess = !isPIProcess && dataService.isMPProcess();

      validateInstrumentsOnInit(isPIProcess, isMPProcess, handler, dataService.getBankConfig()).then((instruments) => {
        onAnswerChange(instrumentsQuestion.uid, instruments)
      }).catch((error) => {
        props.dispatch(displayErrorSnackBar(getErrorMessage(error)))
      }).finally(() => {
        setLoading(false)
      })
    })();

    setStepsData({
      savingsPlan: savingsPlan,
      singleBuyAmount: singleBuyAmount,
      vlPlanEnabled,
      singleInvestmentEnabled: investmentsData.single_investment_enabled,
      savingsPlanEnabled: investmentsData.savings_plan_enabled,
      einzeltitelEnabled: investmentsData.einzeltitel_enabled,
    });

    if (!instrumentsQuestion.answer) {
      handleAnswerChange([]);
    } else if (instrumentsQuestion.answer.length > 0) {
      let shares = [], funds = [];
      splitEinzeltitelTradingInstruments(instrumentsQuestion.answer, {}, shares, funds);
      const result = _.flatten([showFunds ? funds : [], investmentsData.einzeltitel_enabled ? shares : []]);
      handleAnswerChange(result);
    }

    instrumentsQuestion.isValid = function(){

      const hasAmountRelatedErrors = (item) => {
        return !!Object.entries(item.error || {})
          .some(([errorFieldName, errorValue]) => {
            return ['amount', 'savingsPlanAmount'].includes(errorFieldName) && !!errorValue
          })
      }

      const checkInstrumentsDisabledByFilter = (instruments, errorFieldKey='funds_error') => {
        if (_.some(instruments, (item) => item.disabledByFilter)) {
          this[errorFieldKey] = 'Die gewählten Instrumente können nicht verwendet werden';
          return false;
        }
        return true
      };

      const checkInstrumentsAmount = (instruments, errorFieldKey='funds_error') => {
        if(!!instruments.find(i => (!i.amount && !i.transaction_value) || hasAmountRelatedErrors(i))) {
          this[errorFieldKey] = 'Bitte bestätigen Sie alle Beträge für die Instrumente';
        }
      };

      const checkInstrumentRiskScoreConfirmation = (instruments, errorFieldKey='funds_error', errorFieldMessage=undefined) => {
        instruments.forEach((item) => {
          if (instrumentNeedsRiskReasoning(item, dataService._srri)) {
            item.error = {
              ...(item.error || {}),
              risk_score_explanation: 'This field is required' // For now German translate is not required, as we are not displaying it for this case
            }
            this[errorFieldKey] = errorFieldMessage || 'Bitte erfassen Sie eine Begründung für die Order eines Produktes mit einer höheren Risikoklasse als die des Kunden, damit Sie fortfahren können.'
          }
        })
      }

      this.funds_error = this.shares_error = null;

      const items = instrumentsQuestion.answer;

      // Do not validate instruments for open only flow
      if (isOpenOnly) {
        if (_.isEmpty(items)) {
          displayInstrumentsErrors(['Bitte fügen Sie ein Instrument zur Liste hinzu']);
          return false
        }
        return true
      }

      if (instrumentsQuestion.disabledByFilter) {
        displayInstrumentsErrors([instrumentsQuestion.disabledByFilter]);
        return false;
      }

      let shares = [], funds = [];
      splitEinzeltitelTradingInstruments(items, {}, shares, funds);
      const instrumentSourceAnswer = sourceQuestion.answer;

      if (investmentsData.einzeltitel_enabled) {
        if (_.isArray(shares) && shares.length > 0) {

          checkInstrumentsDisabledByFilter(shares, 'shares_error')

          if (!this.shares_error) {
            checkInstrumentsAmount(shares, 'shares_error')
          }

          if (!this.shares_error) {
            checkInstrumentRiskScoreConfirmation(shares, 'shares_error')
          }

          shares.forEach((share) => {
            share.errors = {}
            if (share.order_type !== 'no order' && !share.limit ) {
              share.errors.limit = 'Die gewählte Auswahl ist nicht verfügbar.'
            }

            validateEinzeltitelLimit(share, share.errors,
              (item) => _.get(item, 'calculated.last_price_value'))

          })

        } else {
          this.shares_error = 'Bitte fügen Sie ein Instrument zur Liste hinzu';
        }
      }

      if (showFunds) {
        const baseFondTransaction = _.get(instrumentSourceAnswer, 'baseFondTransaction');
         if(!instrumentSourceAnswer){

          if(_.isArray(funds) && funds.length > 0){

            checkInstrumentsDisabledByFilter(funds)

            if (!this.funds_error) {
              if (vlPlanEnabled && funds.length > 1) {
                this.funds_error = 'Für Vermögenswirksame Leistungen (VL) kann nur ein Investment gewählt werden';
              }
            }

            if (!this.funds_error) {
              checkInstrumentsAmount(funds)
            }

            if (!this.funds_error) {
              // saving plans
              checkNewDesignPlans(funds.filter(i => !!i.savingsPlan), dataService, this)
            }

          } else {
            this.funds_error = 'Bitte fügen Sie ein Instrument zur Liste hinzu';
          }

        } else if((!instrumentSourceAnswer.amount && !instrumentSourceAnswer.savingsPlanAmount) || hasAmountRelatedErrors(instrumentSourceAnswer)) {
          this.funds_error = 'Bitte bestätigen Sie alle Beträge für die Instrumente';
        } else if(instrumentSourceAnswer.savingsPlanAmount){
          checkNewDesignPlans([instrumentSourceAnswer], dataService, this)
        } else if (baseFondTransaction){
          if((!baseFondTransaction.amount && !baseFondTransaction.savingsPlanAmount) || hasAmountRelatedErrors(baseFondTransaction)){
            this.funds_error = 'Bitte bestätigen Sie alle Beträge für die Instrumente';
          } else if(baseFondTransaction.savingsPlanAmount){
            checkNewDesignPlans([baseFondTransaction], dataService, this)
          }
          if(!this.funds_error){
            checkInstrumentRiskScoreConfirmation([baseFondTransaction])
          }
        }

        if (!this.funds_error) {
          if (!instrumentSourceAnswer || instrumentsSourceSrriValidationRequired(instrumentSourceAnswer)) {
            checkInstrumentRiskScoreConfirmation(funds)
          } else if (instrumentSourceAnswer.isInvestmentStrategy) {
            const confirmationRequired = instrumentSourceAnswer.risk_score_confirmation_required
              || instrumentSourceAnswer.experience_confirmation_required;

            if (confirmationRequired && _.isEmpty(instrumentSourceAnswer.risk_score_explanation)) {
              instrumentSourceAnswer.error = {
                ...(instrumentSourceAnswer.error || {}),
                risk_score_explanation: 'This field is required' // For now German translate is not required, as we are not displaying it for this case
              };

              let error = 'Bitte erfassen Sie eine Begründung für die Anlage in einer Vermögensverwaltung mit einer höheren Risikoklasse als die des Kunden, damit Sie fortfahren können.';
              if (instrumentSourceAnswer.risk_score_confirmation_required
                && instrumentSourceAnswer.experience_confirmation_required) {
                error = 'Bitte geben Sie eine Begründung für die Anlage in einer Vermögensverwaltung mit einer höheren Risikoklasse als der des Kunden sowie einem höheren Kenntnis-/Erfahrungsniveau als dem des Kunden an, um fortzufahren.';
              } else if (instrumentSourceAnswer.experience_confirmation_required) {
                error = 'Bitte geben Sie eine Begründung für die Anlage in einer Vermögensverwaltung mit einem höheren Kenntnis-/Erfahrungsniveau als dem des Kunden an, um fortzufahren.'
              }

              this.funds_error = error;
            }
          }
        }
      }

      if (!this.funds_error && !this.shares_error) {
        if (!instrumentSourceAnswer || !instrumentSourceAnswer.isInvestmentStrategy) {
          const averageInstrumentsSRRI = _.get(instrumentSourceAnswer, 'srri') || calculateInstrumentsAverageSRRI(items, (instrument) => !instrument.savingsPlan && instrument.amount)
          if (averageInstrumentsSRRI > dataService.maxRiskThreshold) {
            this[showFunds ? 'funds_error' : 'shares_error'] = `Dieses Ordervolumen bzw. die Orderstruktur ist nicht zulässig, da infolge das durchschnittliche Portfoliorisiko über der zulässigen Kunden Risikoschwelle liegen würde. Bitte ändern sie den Betrag oder die Positionen insgesamt. Das zu erwartende Portfoliorisiko auf Basis der erfassten Orders würde bei "${averageInstrumentsSRRI}" liegen.`
          }
        }
      }

      const sharesHaveErrors = _.some(shares, (share) => !_.isEmpty(share.errors));

      const isValid = !this.funds_error && !this.shares_error && !sharesHaveErrors;
      if(!isValid) {
        displayInstrumentsErrors(_.uniq([this.funds_error, this.shares_error].filter(err => !!err)));
      }
      if(sharesHaveErrors){
        // in case errors are set directly to shares we should re-create answer to re-render
        instrumentsQuestion.answer = [...instrumentsQuestion.answer];
      }
      return isValid;
    };

    return () => {
      props.dispatch(setProductSelectionInvestmentRecomendation(instrumentsQuestion.answer))
    }

  }, []);


  const _validateInstrumentsSource = async (source, handler, errorMessage) => {

    const [validSource, _] = handler.filterInstruments([source])
    if (!validSource.length) {
      return errorMessage
    }

  }

  const _validateInstruments = async (instruments, handler) => {
    await handler.getCustodianAndFilterInstruments(instruments)
  };

  /**
   * Validate selected instruments. Should be called when go back,
   * or page reload (componentDidMount)
   */
  const validateInstrumentsOnInit = async (isPIFlow, isMPFlow, handler, bankConfig) => {
    const bookingDateDisabled = !bankConfig.onboarding_booking_date_possible;
    const planEndDateDisabled = !bankConfig.onboarding_savings_plan_till_date_enabled;
    // check all instruments for custodian availability
    let instruments = [...(instrumentsQuestion.answer || [])];
    const instrumentsSource = sourceQuestion.answer;

    if(!!instrumentsSource || !_.isEmpty(instruments)){
      // clean prev data
      instruments.map(item => {
        delete item.custodian_data;
        delete item.disabledByFilter;
        delete item.disabledByAcceptanceCriterias;
      });

      const sourceType = instrumentsSource && instrumentsSource.sourceType;

      const instrumentsHandlingParams = {
        [INSTRUMENT_SOURCE_TYPE.MODEL_PORTFOLIO]: {
          sourceTypeValid: isMPFlow,
          validateInstrumentsSource: async () =>  {
            await _validateInstruments(instruments, handler);
            await _validateInstrumentsSource(
              instrumentsSource, new ModelPortfolioHandler(),
              'Die ausgewählte das Modellportfolio ist nicht zulässig!')
          }
        },
        [INSTRUMENT_SOURCE_TYPE.INVESTMENT_STRATEGY]: {
          sourceTypeValid: isPIFlow,
          validateInstrumentsSource: async () => {
            await _validateInstruments(instruments, handler);
            await _validateInstrumentsSource(
              instrumentsSource, new InvestmentStrategyHandler(undefined, dataService._target_market),
              'Die ausgewählte Vermögensverwaltung ist nicht zulässig!')
          }
        }
      }[sourceType] || {
        sourceTypeValid: !(isPIFlow || isMPFlow),
        validateInstrumentsSource: async () => await _validateInstruments(instruments, handler)
      };

      if (!instrumentsHandlingParams.sourceTypeValid) {
        instrumentsQuestion.error = instrumentsQuestion.disabledByFilter = !instrumentsSource ? "Für den gewählten Produkttyp besteht nicht die Möglichkeit individuelle Instrumenten auszuwählen." : `Der ausgewählte Produkttyp kann nicht in ${INSTRUMENT_SOURCE_TYPE_LABEL[sourceType]} verwendet werden.`
      } else {
        instrumentsQuestion.error = instrumentsQuestion.disabledByFilter = await instrumentsHandlingParams.validateInstrumentsSource()
      }

      if(instrumentsQuestion.error){
        displayInstrumentsErrors([instrumentsQuestion.error]);
      } else {
        // initial validation in case we have data from diff custodian
        if (!!instrumentsSource){
          if(instrumentsSource.savingsPlanAmount){
            checkNewDesignPlans([instrumentsSource], dataService, instrumentsQuestion)
          }
        } else {
          let removedPlans = [];
          let depositDateCleaned = false, planEndDateCleaned = false;

          instruments = instruments.filter(item => {
            if(!item.disabledByFilter){
              if(bookingDateDisabled && item.deposit_from_date){
                item.deposit_from_type = undefined;
                item.deposit_from_date = undefined;
                depositDateCleaned = true;
              }

              if(!instrCustodianData(item, handler.__custodian).c_savings_plan_possible){
                if(!!item.savingsPlan){
                  removedPlans.push(item);

                  return false;
                } else {
                  // in new design we store plan data in item and just split it
                  delete item.savingPlanData;
                  delete item.savingsPlanAmount;
                }
              } else if(planEndDateDisabled && !!item.savingsPlan && item.savingPlanData.till_date){
                delete item.savingPlanData.till_date;
                planEndDateCleaned = true;
              }
            }

            return true;
          });

          const warnings = [];
          if (depositDateCleaned){
            warnings.push("Die Option “Ausführung Datum” ist für die ausgewählte Depotbank nicht verfügbar. Das Feld wurde zurückgesetzt.")
          }

          if (removedPlans.length > 0){
            warnings.push(
              removedPlans.length === 1
                ? `Produkt ${removedPlans[0].isin} ist für Sparpläne bei der gewählten Depotbank nicht mehr verfügbar. Der Sparplan wurde automatisch entfernt.`
                : `Produkte ${_.join(_.map(removedPlans, 'isin'), ', ')} sind für Sparpläne bei der gewählten Depotbank nicht mehr verfügbar. Die Sparpläne wurden automatisch entfernt.`
            )
          }

          if(planEndDateCleaned){
            warnings.push("Die Option “Letzte Ausführung” ist für die ausgewählte Depotbank nicht verfügbar. Das Feld wurde zurückgesetzt.")
          }

          if(warnings.length > 0){
            props.dispatch(displayWarningSnackBar(_.join(warnings, "\n")));
          }

          checkNewDesignPlans(instruments.filter(i => !!i.savingsPlan), dataService, instrumentsQuestion);
        }
      }
    }

    return instruments

  };

  const NextButton = (
    <PrimaryButton text={SAVE_AND_CONTINUE_BTN_LABEL} onButtonClick={onNextClick} />
  );

  if (loading){
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress className={classes.loadingIndicator}/>
      </div>
    );
  }

  const handleAnswerChange = (answer, cleanSource) => {
    instrumentsQuestion.error = null; // clean error
    onAnswerChange(instrumentsQuestion.uid, answer);

    if (cleanSource) {
      // clean summary answers
      summaryStep && summaryStep.question.forEach(q => q.answer = null);
      onAnswerChange(sourceQuestion.uid, null);
      instrumentsQuestion.disabledByFilter = null
    }
  };

  const handleInstrumentsSourceAnswerChange = (answer) => {
    onAnswerChange(sourceQuestion.uid, answer);
  }

  const confirm = async (asset, cleanSource=true) => {

    let assetsCopy = _.isArray(asset) ? asset : [asset]

    const riskScoreConfirmation = getRiskScoreConfirmation(dataService, assetsCopy)

    if (riskScoreConfirmation && !await modalContext.confirm(riskScoreConfirmation, 'Bitte bestätigen Sie hier')) {
      return false
    }

    if(cleanSource && sourceQuestion.answer) {
      return await modalContext.confirm("Sind Sie sicher? Ihre aktuelle Auswahl wird durch die neue Auswahl ersetzt.");
    } else {
      return true;
    }
  };

  const onDeleteModelPortfolioClick = () => {
    onAnswerChange(sourceQuestion.uid,  null)
    onAnswerChange(instrumentsQuestion.uid, null)
  };

  const setPortfolioBuilderCase = (caseData) => {
    onAnswerChange(portfolioBuilderQuestion.uid, caseData);
  };

  return (
    <div className={classes.stepContainer}>
      <div className={classes.stepScrollContainer}>
        <div className={clsx(classes.stepContent, 'app-page-container')}>
          <Grid container spacing={4} alignItems={"center"}>
            <Grid item xs={12} sm={true}>
              {name && (
                <div className={clsx(classes.stepName, custom_classes && custom_classes.stepName)} data-id={`${uid}-name`}>
                  {name}
                </div>
              )}
              {info_text && (
                <div className={classes.stepInfoText} data-id={`${uid}-info`}>
                  <span className='far fa-info-circle' />
                  &nbsp;
                  {info_text}
                </div>
              )}
              {help_text && (
                <div className={classes.stepHelpText} data-id={`${uid}-help`}>
                  {help_text}
                </div>
              )}
            </Grid>
            {dataService._srri && (
              <Grid item xs={12} sm={'auto'}>
                <RiskProfileCardForProductSelection sri={dataService._srri} />
              </Grid>
            )}
          </Grid>
          {stepsData ? (
            <AssetsSelection
              onAssetsUpdate={handleAnswerChange}
              onInstrumentsSourceAnswerChange={handleInstrumentsSourceAnswerChange}
              instruments={instrumentsQuestion.answer}
              dataService={dataService}
              portfolioBuilderCase={portfolioBuilderQuestion.answer}
              setPortfolioBuilderCase={setPortfolioBuilderCase}
              vlPlanEnabled={stepsData.vlPlanEnabled}
              singleInvestmentEnabled={stepsData.singleInvestmentEnabled}
              savingsPlanEnabled={stepsData.savingsPlanEnabled}
              einzeltitelEnabled={stepsData.einzeltitelEnabled}
              depot={selectedDepot}
              instrumentsSource={sourceQuestion.answer}
              confirmChange={confirm}
              onDeleteModelPortfolioClick={onDeleteModelPortfolioClick}
              instrumentsHandler={instrumentsHandler}
              infoText={(stepsData.custodianData && stepsData.custodianData.is_private_investment && dataService.isOpenOnly && !sourceQuestion.answer)
                ? "Um das leere Depot zu eröffen muss die Vermögensverwaltung ausgewählt werden!"
                : null}
            />
          ) : (
            <Grid item xs={12} style={{marginTop: 16}}>
              <InfoCard isNewDesign cardType={'warning'}>
                Bitte wählen Sie eine Depotbank und eine Depotart aus. Danach zeigen wir Ihnen die verfügbaren Optionen an.
              </InfoCard>
            </Grid>
          )}
        </div>
        <div className={classes.scrollFix} />
      </div>

      <div id={'step_footer_buttons'}>
        <StepFooter rightItems={[...extraRightButtons, NextButton]} />
      </div>
    </div>
  );
};

export default connect()(withStyles(styles)(ProductSelectionStepV2));
